// 数据更新工具函数
// import mockData from '../mockData';  // 移除mockData依赖

// 定义数据类型
export interface MockData {
    contentMgmt: {
        title: string;
        quickAnswerContent: string;
        medicalDisclaimerContent: string;
        mainContent: string;
        summary: string;
    };
    medicationMgmt: {
        content: {
            title: string;
            subText: string;
        };
        medications: Array<{
            priority: number;
            name: string;
            details: string;
            callToActionName: string;
            callToActionLink: string;
        }>;
    };
    bannerMgmt: {
        name: string;
        subText: string;
        callToActionName: string;
        callToActionLink: string;
        image: string;
    };
    tableMgmt: {
        title: string;
        status: string;
        data: Array<{
            priority: number;
            medicationName: string;
            genericFormat: string;
            type: string;
            sideEffectProfile: string;
        }>;
    };
    safetyInfoMgmt: {
        criticalSafetyInformation: string;
        generalSafetyConsiderations: string[];
    };
    faqMgmt: Array<{
        question: string;
        answer: string;
        priority: number;
    }>;
    stickyBannerMgmt: {
        title: string;
        subText: string;
        callToActionName: string;
        callToActionLink: string;
        image: string;
    };
}

// 创建空数据默认结构
const createEmptyData = (): MockData => ({
    contentMgmt: {
        title: '',
        quickAnswerContent: '',
        medicalDisclaimerContent: '',
        mainContent: '',
        summary: '',
    },
    medicationMgmt: {
        content: {
            title: '',
            subText: '',
        },
        medications: [],
    },
    bannerMgmt: {
        name: '',
        subText: '',
        callToActionName: '',
        callToActionLink: '',
        image: '',
    },
    tableMgmt: {
        title: '',
        status: '',
        data: [],
    },
    safetyInfoMgmt: {
        criticalSafetyInformation: '',
        generalSafetyConsiderations: [],
    },
    faqMgmt: [],
    stickyBannerMgmt: {
        title: '',
        subText: '',
        callToActionName: '',
        callToActionLink: '',
        image: '',
    },
});

// Tab数据管理器 - 为每个tab提供独立的数据副本
export class TabDataManager {
    private originalData: MockData;
    private tabData: any = {};
    private hasChanges: boolean = false;
    private tabName: keyof MockData;

    constructor(tabName: keyof MockData, originalData: MockData) {
        this.tabName = tabName;
        this.originalData = this.deepClone(originalData); // 深度克隆，避免引用问题
        // 初始化时深度克隆该tab的数据，保持原始类型
        this.tabData[tabName] = this.deepClone(originalData[tabName]);
    }

    /**
     * 深度克隆数据，保持原始类型
     */
    private deepClone<T>(data: T): T {
        if (data === null || typeof data !== 'object') {
            return data;
        }

        if (Array.isArray(data)) {
            return data.map((item) => this.deepClone(item)) as T;
        }

        if (typeof data === 'object') {
            const cloned: any = {};
            for (const key in data) {
                if (Object.prototype.hasOwnProperty.call(data, key)) {
                    cloned[key] = this.deepClone((data as any)[key]);
                }
            }
            return cloned;
        }

        return data;
    }

    /**
     * 获取tab数据
     */
    getTabData<T extends keyof MockData>(tabName: T): MockData[T] {
        return this.tabData[tabName] as MockData[T];
    }

    /**
     * 直接更新整个tab数据（用于数组类型的数据）
     * @param value 新值
     */
    updateTabDataDirect(value: any): void {
        this.tabData[this.tabName] = value;
        this.hasChanges = true;
        console.log(`Updated entire tab data for ${this.tabName}`);
    }

    /**
     * 更新tab数据中的特定字段
     * @param fieldPath 字段路径，支持嵌套路径如 'content.title'
     * @param value 新值
     */
    updateTabData(fieldPath: string, value: any): void {
        const pathParts = fieldPath.split('.');
        let current = this.tabData[this.tabName] as any;

        // 遍历路径，找到要更新的字段
        for (let i = 0; i < pathParts.length - 1; i++) {
            if (current && typeof current === 'object' && Object.prototype.hasOwnProperty.call(current, pathParts[i])) {
                current = current[pathParts[i]];
                this.hasChanges = true;
            } else {
                console.error(`Path ${fieldPath} not found in tab ${this.tabName}`);
                return;
            }
        }

        const lastField = pathParts[pathParts.length - 1];
        if (current && Object.prototype.hasOwnProperty.call(current, lastField)) {
            current[lastField] = value;
            this.hasChanges = true;
            console.log(`Updated ${fieldPath} in ${this.tabName}`);
        } else {
            console.error(`Field ${lastField} not found in path ${fieldPath}`);
        }
    }

    /**
     * 更新数组中的特定项
     * @param arrayField 数组字段名
     * @param index 数组索引
     * @param value 新值
     */
    updateArrayItem(arrayField: string, index: number, value: any): void {
        const tabData = this.tabData[this.tabName] as any;
        if (tabData && tabData[arrayField] && Array.isArray(tabData[arrayField])) {
            if (index >= 0 && index < tabData[arrayField].length) {
                tabData[arrayField][index] = value;
                this.hasChanges = true;
                console.log(`Updated array item at index ${index} in ${arrayField}`);
            } else {
                console.error(`Index ${index} out of bounds for array ${arrayField}`);
            }
        } else {
            console.error(`Array field ${arrayField} not found in tab ${this.tabName}`);
        }
    }

    /**
     * 添加新项到数组
     * @param arrayField 数组字段名
     * @param value 新项
     */
    addArrayItem(arrayField: string, value: any): void {
        const tabData = this.tabData[this.tabName] as any;
        if (tabData) {
            if (Array.isArray(tabData)) {
                tabData.push(value);
            } else if (tabData[arrayField] && Array.isArray(tabData[arrayField])) {
                tabData[arrayField].push(value);
            } else {
                tabData[arrayField] = [value];
            }
            this.hasChanges = true;
            console.log(`Added new item to array ${arrayField}`);
        } else {
            console.error(`Array field ${arrayField} not found in tab ${this.tabName}`);
        }
    }

    /**
     * 删除数组中的特定项
     * @param arrayField 数组字段名
     * @param index 要删除的索引
     */
    removeArrayItem(arrayField: string, index: number): void {
        const tabData = this.tabData[this.tabName] as any;
        if (tabData && tabData[arrayField] && Array.isArray(tabData[arrayField])) {
            if (index >= 0 && index < tabData[arrayField].length) {
                tabData[arrayField].splice(index, 1);
                this.hasChanges = true;
                console.log(`Removed item at index ${index} from array ${arrayField}`);
            } else {
                console.error(`Index ${index} out of bounds for array ${arrayField}`);
            }
        } else {
            console.error(`Array field ${arrayField} not found in tab ${this.tabName}`);
        }
    }

    /**
     * 检查是否有未保存的更改
     */
    hasUnsavedChanges(): boolean {
        return this.hasChanges;
    }

    /**
     * 保存更改到原始数据
     */
    saveChanges(): void {
        if (this.hasChanges) {
            // 更新originalData中对应tab的数据
            (this.originalData as any)[this.tabName] = this.deepClone(this.tabData[this.tabName]);
            this.hasChanges = false;
            console.log(`Changes saved for tab: ${this.tabName}`);
        }
    }

    /**
     * 放弃更改，恢复到原始数据
     */
    discardChanges(): void {
        this.tabData[this.tabName] = this.deepClone(this.originalData[this.tabName]);
        this.hasChanges = false;
        console.log(`Changes discarded for tab: ${this.tabName}`);
    }

    /**
     * 获取原始数据（用于比较）
     */
    getOriginalData<T extends keyof MockData>(tabName: T): MockData[T] {
        return this.deepClone(this.originalData[tabName]) as MockData[T];
    }

    /**
     * 获取合并后的数据（包含修改的字段和原始字段）
     */
    getMergedData<T extends keyof MockData>(tabName: T): MockData[T] {
        return this.deepClone(this.tabData[tabName]) as MockData[T];
    }
}

// 数据管理器类
export class DataManager {
    private currentData: MockData;
    private tabManagers: Map<string, TabDataManager> = new Map();

    constructor(initialData?: MockData) {
        // 如果没有传入初始数据，使用mockData
        this.currentData = initialData ? this.deepClone(initialData) : this.deepClone(createEmptyData());
    }

    /**
     * 深度克隆数据，保持原始类型
     */
    private deepClone<T>(data: T): T {
        if (data === null || typeof data !== 'object') {
            return data;
        }

        if (Array.isArray(data)) {
            return data.map((item) => this.deepClone(item)) as T;
        }

        if (typeof data === 'object') {
            const cloned: any = {};
            for (const key in data) {
                if (Object.prototype.hasOwnProperty.call(data, key)) {
                    cloned[key] = this.deepClone((data as any)[key]);
                }
            }
            return cloned;
        }

        return data;
    }

    /**
     * 为指定tab创建数据管理器
     */
    createTabManager(tabName: keyof MockData): TabDataManager {
        const tabManager = new TabDataManager(tabName, this.currentData);
        this.tabManagers.set(tabName as string, tabManager);
        return tabManager;
    }

    /**
     * 获取指定tab的数据管理器
     */
    getTabManager(tabName: keyof MockData): TabDataManager | undefined {
        return this.tabManagers.get(tabName as string);
    }

    /**
     * 保存所有tab的更改
     */
    saveAllTabChanges(): void {
        this.tabManagers.forEach((tabManager, tabName) => {
            // if (tabManager.hasUnsavedChanges()) {
            // 先获取最新的tab数据（在保存之前）
            const latestTabData = tabManager.getMergedData(tabName as keyof MockData);

            // 保存tab的更改到originalData
            tabManager.saveChanges();

            // 将最新的数据同步到currentData
            (this.currentData as any)[tabName] = latestTabData;
            // }
        });
    }

    /**
     * 检查是否有任何tab有未保存的更改
     */
    hasAnyUnsavedChanges(): boolean {
        return Array.from(this.tabManagers.values()).some((tabManager) =>
            tabManager.hasUnsavedChanges(),
        );
    }

    /**
     * 获取合并后的完整数据（包含所有修改和原始数据）
     */
    getMergedData(): MockData {
        // 先保存所有tab的更改到currentData，确保数据同步
        this.saveAllTabChanges();

        // 返回currentData的深度克隆，因为此时currentData已经包含了所有最新的更改
        return this.deepClone(this.currentData);
    }

    /**
     * 获取合并后的完整数据（不保存更改，仅获取当前状态）
     */
    getMergedDataWithoutSaving(): MockData {
        // 直接返回currentData的深度克隆，不保存更改
        return this.deepClone(this.currentData);
    }

    /**
     * 更新指定模块的指定字段
     * @param moduleName 模块名称 (如 'contentMgmt', 'medicationMgmt' 等)
     * @param fieldPath 字段路径 (如 'title', 'content.title' 等)
     * @param value 新值
     */
    updateData(moduleName: keyof MockData, fieldPath: string, value: any): void {
        if (!this.currentData[moduleName]) {
            console.error(`Module ${moduleName} not found in currentData`);
            return;
        }

        const module = this.currentData[moduleName] as any;
        const pathParts = fieldPath.split('.');

        if (pathParts.length === 1) {
            // 直接字段更新
            if (Object.prototype.hasOwnProperty.call(module, fieldPath)) {
                module[fieldPath] = value;
            } else {
                console.error(`Field ${fieldPath} not found in module ${moduleName}`);
            }
        } else {
            // 嵌套字段更新
            let current = module;
            for (let i = 0; i < pathParts.length - 1; i++) {
                if (current && typeof current === 'object' && Object.prototype.hasOwnProperty.call(current, pathParts[i])) {
                    current = current[pathParts[i]];
                } else {
                    console.error(`Path ${fieldPath} not found in module ${moduleName}`);
                    return;
                }
            }
            const lastField = pathParts[pathParts.length - 1];
            if (current && Object.prototype.hasOwnProperty.call(current, lastField)) {
                current[lastField] = value;
            } else {
                console.error(`Field ${lastField} not found in path ${fieldPath}`);
            }
        }
    }

    /**
     * 更新数组中的特定项
     * @param moduleName 模块名称
     * @param arrayField 数组字段名
     * @param index 数组索引
     * @param value 新值
     */
    updateArrayItem(moduleName: keyof MockData, arrayField: string, index: number, value: any): void {
        const module = this.currentData[moduleName] as any;
        if (module && module[arrayField] && Array.isArray(module[arrayField])) {
            if (index >= 0 && index < module[arrayField].length) {
                module[arrayField][index] = value;
            } else {
                console.error(`Index ${index} out of bounds for array ${arrayField}`);
            }
        } else {
            console.error(`Array field ${arrayField} not found in module ${moduleName}`);
        }
    }

    /**
     * 添加新项到数组
     * @param moduleName 模块名称
     * @param arrayField 数组字段名
     * @param value 新项
     */
    addArrayItem(moduleName: keyof MockData, arrayField: string, value: any): void {
        const module = this.currentData[moduleName] as any;
        if (module && module[arrayField] && Array.isArray(module[arrayField])) {
            module[arrayField].push(value);
        } else {
            console.error(`Array field ${arrayField} not found in module ${moduleName}`);
        }
    }

    /**
     * 删除数组中的特定项
     * @param moduleName 模块名称
     * @param arrayField 数组字段名
     * @param index 要删除的索引
     */
    removeArrayItem(moduleName: keyof MockData, arrayField: string, index: number): void {
        const module = this.currentData[moduleName] as any;
        if (module && module[arrayField] && Array.isArray(module[arrayField])) {
            if (index >= 0 && index < module[arrayField].length) {
                module[arrayField].splice(index, 1);
            } else {
                console.error(`Index ${index} out of bounds for array ${arrayField}`);
            }
        } else {
            console.error(`Array field ${arrayField} not found in module ${moduleName}`);
        }
    }

    /**
     * 重置数据为空
     */
    resetData(): void {
        this.currentData = this.deepClone(createEmptyData());
        this.tabManagers.clear();
    }

    /**
     * 更新初始数据（用于从API获取数据后更新）
     * @param newData 新的数据
     */
    updateInitialData(newData: MockData): void {
        this.currentData = this.deepClone(newData);
        // 清除所有tab管理器，强制重新创建
        // this.tabManagers.clear();
        console.log('Initial data updated:', this.currentData);
    }

    /**
     * 获取当前数据
     */
    getCurrentData(): MockData {
        return this.deepClone(this.currentData);
    }

    /**
     * 获取包含所有修改的最终数据（用于保存到服务器）
     * 这个方法会合并所有tab的修改，返回完整的最终数据
     */
    getInitialData(): MockData {
        // 先保存所有tab的更改，然后获取最新数据
        this.saveAllTabChanges();

        return this.deepClone(this.currentData);
    }

    /**
     * 获取指定模块的数据
     */
    getModuleData<K extends keyof MockData>(moduleName: K): MockData[K] {
        return this.deepClone(this.currentData[moduleName]);
    }

    /**
     * 替换数据
     */
    replaceData(newData: MockData): void {
        this.currentData = this.deepClone(newData);
        this.tabManagers.clear();
    }

    /**
     * 保存数据到服务器
     * @param onSuccess 成功回调
     * @param onError 错误回调
     */
    async saveDataToServer(
        onSuccess?: () => void,
        onError?: (error: any) => void,
    ): Promise<void> {
        try {
            // 先保存所有tab的更改到currentData
            this.saveAllTabChanges();

            // 获取合并后的完整数据（包含所有修改和原始数据）
            const dataToSave = this.getMergedData();

            // 这里应该调用实际的API
            console.log('Saving data to server:', dataToSave);

            // 模拟API调用 - 实际使用时替换为真实的API调用
            // const response = await fetch('/api/updatePageData', {
            //   method: 'POST',
            //   headers: {
            //     'Content-Type': 'application/json',
            //   },
            //   body: JSON.stringify(dataToSave),
            // });

            // if (!response.ok) {
            //   throw new Error('Failed to save data');
            // }

            console.log('Data saved successfully');
            onSuccess?.();
        } catch (error) {
            console.error('Error saving data:', error);
            onError?.(error);
        }
    }
}

// 创建数据管理器的工厂函数
export const createDataManager = (initialData?: MockData): DataManager => {
    return new DataManager(initialData);
};

// 使用示例：
// 1. 开发环境使用mockData
// const devDataManager = createDataManager(); // 使用mockData

// 2. 生产环境使用API数据
// const apiData = await fetch('/api/pageData/123');
// const prodDataManager = createDataManager(apiData);

// 3. 为每个tab创建独立的数据管理器
// const contentTabManager = dataManager.createTabManager('contentMgmt');
// const medicationTabManager = dataManager.createTabManager('medicationMgmt');

// 4. 在tab组件中使用
// const tabData = contentTabManager.getTabData('contentMgmt');
// contentTabManager.updateTabData('title', 'New Title');

// 5. 保存时同步所有更改
// await dataManager.saveDataToServer();

// ===== 数据保存机制示例 =====
//
// 假设原始数据包含以下字段：
// const originalData = {
//     bannerMgmt: {
//         name: "Original Banner",
//         subText: "Original subtitle",
//         callToActionName: "Learn More",
//         callToActionLink: "#",
//         image: "https://example.com/original-image.jpg"  // 这个字段不会被编辑
//     }
// };
//
// 用户只修改了name字段：
// bannerTabManager.updateTabData('name', 'Updated Banner Name');
//
// 保存时，系统会：
// 1. 获取合并后的数据（包含修改的name和原始的image等字段）
// 2. 将完整数据发送到服务器
// 3. 确保未修改的image字段被保留
//
// 最终保存的数据：
// {
//     bannerMgmt: {
//         name: "Updated Banner Name",                    // 修改的字段
//         subText: "Original subtitle",                   // 保留原始值
//         callToActionName: "Learn More",                 // 保留原始值
//         callToActionLink: "#",                          // 保留原始值
//         image: "https://example.com/original-image.jpg" // 保留原始值
//     }
// }
//
// 这样可以确保：
// - 未编辑的字段（如image）不会被覆盖为空值
// - 只有被修改的字段会被更新
// - 数据完整性得到保证

// ===== 智能更新工具函数 =====
/**
 * 智能更新数据，只更新实际修改的字段
 * @param tabManager TabDataManager实例
 * @param newData 新的数据
 * @param currentData 当前数据
 * @param fieldsToUpdate 需要检查更新的字段列表
 */
export const smartUpdateData = <T extends Record<string, any>>(
    tabManager: TabDataManager,
    newData: Partial<T>,
    currentData: T,
    fieldsToUpdate: (keyof T)[],
) => {
    fieldsToUpdate.forEach((field) => {
        const fieldName = field as string;
        const newValue = (newData as any)[fieldName];
        const currentValue = (currentData as any)[fieldName];

        // 只有当新值存在且与当前值不同时才更新
        if (newValue !== undefined && newValue !== currentValue) {
            tabManager.updateTabData(fieldName, newValue);
            console.log(`Updated field: ${fieldName} from "${currentValue}" to "${newValue}"`);
        } else {
            console.log(`Skipped field: ${fieldName} (no change or undefined)`);
        }
    });
};

// 使用示例：
// const fieldsToUpdate = ['name', 'subText', 'callToActionName', 'callToActionLink'];
// smartUpdateData(bannerTabManager, formData, bannerMgmtData, fieldsToUpdate);

// 临时ID处理函数
export const generateTempId = (): string => {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// 为数组项添加临时ID
export const addTempIdsToArray = <T extends Record<string, any>>(items: T[]): (T & { _tempId: string })[] => {
    return items.map((item) => ({
        ...item,
        _tempId: (item as any)._tempId || generateTempId(),
    }));
};

// 从数组项中移除临时ID
export const removeTempIdsFromArray = <T extends Record<string, any>>(items: T[]): Omit<T, '_tempId'>[] => {
    return items.map((item) => {
        const { _tempId, ...itemWithoutTempId } = item;
        return itemWithoutTempId;
    });
};

// 为整个数据对象添加临时ID
export const addTempIdsToData = (data: MockData): MockData => {
    return {
        ...data,
        medicationMgmt: {
            ...data.medicationMgmt,
            medications: addTempIdsToArray(data.medicationMgmt.medications),
        },
        faqMgmt: addTempIdsToArray(data.faqMgmt),
    };
};

// 从整个数据对象移除临时ID（用于保存）
export const removeTempIdsFromData = (data: MockData): MockData => {
    return {
        ...data,
        medicationMgmt: {
            ...data.medicationMgmt,
            medications: removeTempIdsFromArray(data.medicationMgmt.medications),
        },
        faqMgmt: removeTempIdsFromArray(data.faqMgmt),
    };
};
